import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "./Reveal";
import { Heart, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLang } from "@/context/LanguageContext";
import { curInfo } from "@/lib/currency";
import { EditableText } from "@/components/site/edit/EditableText";

const PRESETS = [18, 36, 72, 180, 360];

export function Donate() {
  const { t } = useLang();
  const [amount, setAmount] = useState(72);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [payLoading, setPayLoading] = useState("");
  const [pp, setPp] = useState({ stripe_enabled: true, paypal_enabled: false, donation_currency: "eur" });

  useEffect(() => {
    api.get("/payments/config").then((r) => setPp(r.data)).catch(() => {});
  }, []);

  const cur = pp.donation_currency || "eur";
  const curSym = curInfo(cur).symbol;
  const effectiveAmount = custom ? Number(custom) : amount;

  const donate = async () => {
    if (!effectiveAmount || effectiveAmount < 1) {
      toast.error(t("donate.err_amount"));
      return;
    }
    setLoading(true);
    setPayLoading("stripe");
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: effectiveAmount,
        frequency,
        donor_name: name,
        donor_email: email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("donate.err_checkout"));
      setLoading(false);
      setPayLoading("");
    }
  };

  const donatePaypal = async () => {
    if (!effectiveAmount || effectiveAmount < 1) {
      toast.error(t("donate.err_amount"));
      return;
    }
    setLoading(true);
    setPayLoading("paypal");
    try {
      const { data } = await api.post("/donations/paypal/create", {
        amount: effectiveAmount,
        frequency: "one-time",
        donor_name: name,
        donor_email: email,
        origin_url: window.location.origin,
      });
      window.location.href = data.approve_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("donate.err_checkout"));
      setLoading(false);
      setPayLoading("");
    }
  };

  const showPaypal = pp.paypal_enabled && frequency === "one-time";

  return (
    <section id="donate" data-testid="donate-section" className="relative z-10 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="donate.eyebrow" as="span" /></span>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
            <EditableText k="donate.title1" as="span" /> <span className="italic text-[#BE923C]"><EditableText k="donate.title_italic" as="span" /></span>
          </h2>
          <p className="mt-6 text-[#475569] leading-relaxed max-w-md">
            <EditableText k="donate.desc" as="span" />
          </p>
          <div className="mt-8 flex items-center gap-3 text-sm text-[#475569]">
            <span className="w-10 h-10 rounded-full bg-[#8C9B86]/20 flex items-center justify-center text-[#8C9B86]">
              <Heart size={18} />
            </span>
            <EditableText k="donate.heart" as="span" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            data-testid="donation-widget"
            className="rounded-3xl bg-white border border-[#0F172A]/5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] p-7 sm:p-9"
          >
            <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-[#E8E6E1] mb-7">
              {["one-time", "monthly"].map((f) => (
                <button
                  key={f}
                  data-testid={`freq-${f}`}
                  onClick={() => setFrequency(f)}
                  className={`relative rounded-full py-2.5 text-sm font-semibold transition-colors ${
                    frequency === f ? "text-white" : "text-[#475569]"
                  }`}
                >
                  {frequency === f && (
                    <motion.span layoutId="freq-pill" className="absolute inset-0 rounded-full bg-[#0F172A]" />
                  )}
                  <span className="relative z-10 capitalize">{f === "one-time" ? t("donate.onetime") : t("donate.monthly")}</span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  data-testid={`amount-${p}`}
                  onClick={() => { setAmount(p); setCustom(""); }}
                  className={`rounded-2xl py-4 font-serif text-xl transition-colors border ${
                    !custom && amount === p
                      ? "bg-[#BE923C] text-white border-[#BE923C]"
                      : "bg-[#F9F8F6] border-[#0F172A]/10 hover:border-[#BE923C]"
                  }`}
                >
                  {curInfo(cur).symbolBefore ? `${curSym}${p}` : `${p} ${curSym}`}
                </button>
              ))}
              <div className="relative col-span-3 sm:col-span-1 flex items-center">
                <span className="absolute left-4 text-[#475569] font-serif text-xl">{curSym}</span>
                <Input
                  data-testid="amount-custom"
                  type="number"
                  min={1}
                  placeholder={t("donate.other")}
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className="pl-8 h-[60px] rounded-2xl font-serif text-xl border-[#0F172A]/10"
                />
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <Input data-testid="donor-name" placeholder={t("donate.name_ph")} value={name}
                onChange={(e) => setName(e.target.value)} className="rounded-xl border-[#0F172A]/10" />
              <Input data-testid="donor-email" type="email" placeholder={t("donate.email_ph")} value={email}
                onChange={(e) => setEmail(e.target.value)} className="rounded-xl border-[#0F172A]/10" />
            </div>

            <button
              data-testid="donate-submit"
              onClick={donate}
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#BE923C] hover:bg-[#9E7829] text-white font-semibold py-4 transition-colors disabled:opacity-60"
            >
              <CreditCard size={18} />
              {payLoading === "stripe"
                ? t("donate.redirecting")
                : `${showPaypal ? t("donate.card") : t("donate.button")} · ${curInfo(cur).symbolBefore ? `${curSym}${effectiveAmount || 0}` : `${effectiveAmount || 0} ${curSym}`}${frequency === "monthly" ? t("donate.permonth") : ""}`}
            </button>

            {pp.paypal_enabled && frequency === "monthly" && (
              <p data-testid="paypal-monthly-note" className="mt-3 text-center text-xs text-[#475569]">
                <EditableText k="donate.paypal_monthly_note" as="span" />
              </p>
            )}

            {showPaypal && (
              <>
                <div className="my-4 flex items-center gap-3 text-xs text-[#94A3B8]">
                  <span className="h-px flex-1 bg-[#0F172A]/10" />
                  <EditableText k="donate.or" as="span" />
                  <span className="h-px flex-1 bg-[#0F172A]/10" />
                </div>
                <button
                  data-testid="donate-paypal-btn"
                  onClick={donatePaypal}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FFC439] hover:bg-[#f0b62d] text-[#003087] font-bold py-4 transition-colors disabled:opacity-60"
                >
                  {payLoading === "paypal"
                    ? t("donate.redirecting")
                    : <><span className="font-extrabold italic">Pay<span className="text-[#0070E0]">Pal</span></span> · {curInfo(cur).symbolBefore ? `${curSym}${effectiveAmount || 0}` : `${effectiveAmount || 0} ${curSym}`}</>}
                </button>
              </>
            )}

            <p className="mt-4 text-center text-xs text-[#475569]">
              {pp.paypal_enabled ? t("donate.secure") : t("donate.secure_stripe")}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
